import {isAxiosError} from 'axios';
import clsx from 'clsx';
import React, {useState, useCallback, useMemo} from 'react';
import {TextField, Radio, styled} from '@mui/material';
import {
  Button,
  Body,
  Headline,
  Icon,
  Label,
  Modal,
  designSystemToken,
} from '@lightricks/react-design-system';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import type {
  Segment,
  SegmentFilters,
  SegmentScope,
} from '@/types/models/segments/segment';
import {
  SEGMENT_SCOPE,
  SEGMENT_SCOPE_ORDER,
  SEGMENT_SCOPE_SHORT_LABEL,
  SEGMENT_SCOPE_VISIBILITY_LABEL,
  isCampaignSegment,
} from '@/types/models/segments/segment';
import type {GroupedDisplayItems} from '@/views/creators/creator-crm/components/active-filters-bar/displayItemTypes';
import FilterChipsList from '@/views/creators/creator-crm/components/filter-chips-list';
import AnimatedCaret from '@/components/animated-caret';
import ButtonGroup from '@/components/button-group';
import Dropdown, {type DropdownOption} from '@/components/dropdown';
import OptionButton from '@/components/option-button';
import ToggleButton from '@/components/toggle-button';
import {
  useCreateSegmentMutation,
  useUpdateSegmentMutation,
} from '@/hooks/mutations/segments';
import useBrandId from '@/hooks/use-brand-id';
import styles from './SegmentManagementModal.module.scss';

export type SegmentManagementModalProps = {
  open: boolean;
  onClose: () => void;
  currentFilters: SegmentFilters;
  existingSegments: Segment[];
  groupedDisplayItems: GroupedDisplayItems[];
  onSegmentSaved?: (segmentId: string, segment: Segment) => void;
  activeSegment?: Segment | null;
  currentUserId?: string | null;
  brandName?: string;
  organizationName?: string;
};

type ModalMode = 'create' | 'update';

const MODE_OPTIONS: {value: ModalMode; localeLabelKey: string}[] = [
  {value: 'create', localeLabelKey: 'Create New'},
  {value: 'update', localeLabelKey: 'Update Existing'},
];

const SCOPE_BUTTONS = SEGMENT_SCOPE_ORDER.map((value) => ({
  value,
  label: SEGMENT_SCOPE_VISIBILITY_LABEL[value],
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    'borderRadius': '12px',
    'height': '54px',
    'border': `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
    'fontSize': '14px',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
  },
}));

const StyledRadio = styled(Radio)(() => ({
  'padding': 0,
  'marginTop': '1px',
  'color': designSystemToken('semantic.bg.neutral-inverse'),
  '&.Mui-checked': {
    color: designSystemToken('semantic.fg.primary'),
  },
}));

function getScopeSubtitle(
  scopeValue: SegmentScope,
  brandName?: string,
  organizationName?: string
): string {
  switch (scopeValue) {
    case SEGMENT_SCOPE.USER:
      return translate('Private - only visible to you');
    case SEGMENT_SCOPE.BRAND:
      return brandName
        ? translate('Visible to everyone in {name}', {name: brandName})
        : translate('Visible to everyone in your team');
    case SEGMENT_SCOPE.ORGANIZATION:
      return organizationName
        ? translate('Visible to everyone in {name}', {name: organizationName})
        : translate('Visible to everyone in your organization');
    default:
      return '';
  }
}

function getSegmentErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const errors = error.response?.data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors[0];
    }
    if (typeof errors === 'string') {
      return errors;
    }
  }
  return translate('Something went wrong. Please try again.');
}

const UPDATE_WARNING_TEXT: Record<SegmentScope, string> = {
  [SEGMENT_SCOPE.USER]: 'Saving will update {name} for you only.',
  [SEGMENT_SCOPE.BRAND]: 'Saving will update {name} for everyone on your team.',
  [SEGMENT_SCOPE.ORGANIZATION]:
    'Saving will update {name} for everyone in your organization.',
};

function getUpdateWarningText(segment: Segment): string {
  const template =
    UPDATE_WARNING_TEXT[segment.scope] ||
    UPDATE_WARNING_TEXT[SEGMENT_SCOPE.BRAND];
  return translate(template, {name: segment.name});
}

function SegmentManagementModal({
  open,
  onClose,
  currentFilters,
  existingSegments,
  groupedDisplayItems,
  onSegmentSaved,
  activeSegment,
  currentUserId,
  brandName,
  organizationName,
}: SegmentManagementModalProps) {
  const brandId = useBrandId();
  const merchantId = brandId ?? null;
  const createMutation = useCreateSegmentMutation();
  const updateMutation = useUpdateSegmentMutation();

  const [mode, setMode] = useState<ModalMode>('create');
  const [segmentName, setSegmentName] = useState('');
  const [scope, setScope] = useState<SegmentScope>(SEGMENT_SCOPE.BRAND);
  const [selectedSegmentToOverwrite, setSelectedSegmentToOverwrite] =
    useState<string>('');

  const allSegments = useMemo(
    () => existingSegments.filter((s) => !isCampaignSegment(s)),
    [existingSegments]
  );

  const segmentToOverwrite = useMemo(
    () => allSegments.find((s) => s.id === selectedSegmentToOverwrite),
    [allSegments, selectedSegmentToOverwrite]
  );

  const segmentDropdownOptions: DropdownOption[] = useMemo(
    () =>
      allSegments.map((s) => {
        const isOwned =
          currentUserId != null && s.createdByDashboardUserId === currentUserId;
        const isSelected = s.id === selectedSegmentToOverwrite;
        return {
          id: s.id,
          label: s.name,
          subtitle: SEGMENT_SCOPE_SHORT_LABEL[s.scope] || '',
          disabled: !isOwned,
          endIcon: isSelected ? (
            <Icon name="Actions-Accept-Small" appearance="neutral" />
          ) : null,
        };
      }),
    [allSegments, currentUserId, selectedSegmentToOverwrite]
  );

  const nameToSave = segmentName.trim();

  const handleClose = useCallback(() => {
    setSegmentName('');
    setScope(SEGMENT_SCOPE.BRAND);
    setMode('create');
    setSelectedSegmentToOverwrite('');
    onClose();
  }, [onClose]);

  const handleCreate = useCallback(async () => {
    if (!nameToSave) {
      return;
    }
    try {
      const created = await createMutation.mutateAsync({
        name: nameToSave,
        merchantId,
        scope,
        filters: currentFilters,
      });
      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: translate('Filters saved'),
      });
      onSegmentSaved?.(created.id, created);
      handleClose();
    } catch (error) {
      raiseFlashMessage({
        status: 'error',
        mode: 'light',
        message: getSegmentErrorMessage(error),
      });
    }
  }, [
    nameToSave,
    merchantId,
    scope,
    currentFilters,
    createMutation,
    handleClose,
    onSegmentSaved,
  ]);

  const handleOverwrite = useCallback(async () => {
    if (!segmentToOverwrite) {
      return;
    }
    try {
      const updated = await updateMutation.mutateAsync({
        id: segmentToOverwrite.id,
        payload: {
          name: segmentToOverwrite.name,
          merchantId,
          scope: segmentToOverwrite.scope,
          filters: currentFilters,
        },
      });
      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: translate('Filters saved'),
      });
      onSegmentSaved?.(updated.id, updated);
      handleClose();
    } catch (error) {
      raiseFlashMessage({
        status: 'error',
        mode: 'light',
        message: getSegmentErrorMessage(error),
      });
    }
  }, [
    segmentToOverwrite,
    merchantId,
    currentFilters,
    updateMutation,
    handleClose,
    onSegmentSaved,
  ]);

  const hasActiveFilters = groupedDisplayItems.length > 0;
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  // Pre-select the active segment when switching to update mode
  const handleModeChange = useCallback(
    (newMode: ModalMode) => {
      setMode(newMode);
      if (
        newMode === 'update' &&
        activeSegment &&
        !isCampaignSegment(activeSegment) &&
        currentUserId != null &&
        activeSegment.createdByDashboardUserId === currentUserId
      ) {
        setSelectedSegmentToOverwrite(activeSegment.id);
      }
    },
    [activeSegment, currentUserId]
  );

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      showCloseButton
      size="large"
      className={styles.modal}
    >
      <div className={styles.container}>
        <Headline size="md" className={styles.title}>
          {translate('Save Filters')}
        </Headline>

        {/* Mode toggle */}
        <div className={styles.field}>
          <ToggleButton
            options={MODE_OPTIONS.map((modeOption) => ({
              ...modeOption,
              label: translate(modeOption.localeLabelKey),
            }))}
            value={mode}
            onChange={(val) => handleModeChange(val as ModalMode)}
            borderRadius="999px"
            padding="4px"
            settings={{
              backgroundColor: designSystemToken('semantic.bg.tertiary'),
              selectedOptionColor: designSystemToken('semantic.bg.secondary'),
              optionBorderRadius: '999px',
            }}
          />
        </div>

        {!hasActiveFilters ? (
          <Body size="lg" className={styles.error}>
            {translate('Add at least one filter before saving.')}
          </Body>
        ) : null}

        {hasActiveFilters ? (
          <div className={clsx(styles.field, styles.activeFiltersBar)}>
            <FilterChipsList groupedDisplayItems={groupedDisplayItems} />
          </div>
        ) : null}

        {mode === 'create' ? (
          <>
            <div className={styles.field}>
              <Label size="md" className={styles.fieldTitle}>
                {translate('Filter Name')}
              </Label>
              <StyledTextField
                variant="outlined"
                fullWidth
                value={segmentName}
                onChange={(e) => setSegmentName(e.target.value.slice(0, 50))}
                placeholder={translate('e.g. NYC Food Creators')}
                disabled={!hasActiveFilters}
                inputProps={{maxLength: 50}}
              />
              <Body size="sm" className={styles.charCount}>
                {segmentName.length}/50
              </Body>
            </div>
            <ButtonGroup
              label={translate('Visibility')}
              buttons={SCOPE_BUTTONS}
              selectedButtonValue={scope}
              onChange={(selection) => {
                const btn = selection as {value: SegmentScope};
                setScope(btn.value);
              }}
              readOnly={!hasActiveFilters}
              containerClassName={styles.visibilityOptions}
              renderButton={(button, isActive, onClick) => {
                const scopeValue = button.value as SegmentScope;
                return (
                  <OptionButton
                    key={button.value}
                    button={button}
                    isActive={isActive}
                    onClick={onClick}
                    disabled={!hasActiveFilters}
                    className={styles.visibilityOptionButton}
                  >
                    <StyledRadio checked={isActive} size="medium" />
                    <div className={styles.visibilityText}>
                      <Label size="md">
                        {translate(SEGMENT_SCOPE_VISIBILITY_LABEL[scopeValue])}
                      </Label>
                      <Body size="sm" className={styles.visibilitySubtitle}>
                        {getScopeSubtitle(
                          scopeValue,
                          brandName,
                          organizationName
                        )}
                      </Body>
                    </div>
                  </OptionButton>
                );
              }}
            />
          </>
        ) : null}

        {mode === 'update' ? (
          <div className={styles.field}>
            <Label size="md" className={styles.fieldTitle}>
              {translate('Select a saved filter to update')}
            </Label>
            <Dropdown
              label={
                segmentToOverwrite?.name ||
                translate('Choose a saved filter...')
              }
              options={segmentDropdownOptions}
              selectedIds={
                selectedSegmentToOverwrite ? [selectedSegmentToOverwrite] : []
              }
              onChange={(option) => setSelectedSegmentToOverwrite(option.id)}
              selectionMode="single"
              menuMaxHeight={400}
              shouldScrollToSelectedItem
              matchTriggerWidth
              renderTrigger={({onClick, isOpen}) => (
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={onClick}
                  className={clsx(
                    styles.dropdownTrigger,
                    segmentToOverwrite && styles.dropdownTriggerActive
                  )}
                  endIcon={
                    <AnimatedCaret isOpen={isOpen} appearance="neutral" />
                  }
                >
                  <Body
                    size="md"
                    color={
                      segmentToOverwrite
                        ? designSystemToken('semantic.fg.primary')
                        : designSystemToken('semantic.fg.inverse-secondary')
                    }
                  >
                    {segmentToOverwrite?.name ||
                      translate('Choose a saved filter...')}
                  </Body>
                </Button>
              )}
            />
            {segmentToOverwrite ? (
              <div className={styles.warningBanner}>
                <Icon
                  size="small"
                  name="Actions-Alert-Line"
                  appearance="neutral"
                  color={designSystemToken('semantic.fg.warning')}
                />
                <Body size="sm" className={styles.warningBannerText}>
                  {getUpdateWarningText(segmentToOverwrite)}
                </Body>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={styles.actions}>
          <Button
            size="medium"
            appearance="neutral"
            mode="plain"
            onClick={handleClose}
          >
            {translate('Cancel')}
          </Button>
          <Button
            size="medium"
            appearance="neutral"
            mode="filled"
            onClick={mode === 'create' ? handleCreate : handleOverwrite}
            disabled={
              mode === 'create'
                ? !nameToSave || isSaving || !hasActiveFilters
                : !segmentToOverwrite || isSaving || !hasActiveFilters
            }
          >
            {translate(mode === 'create' ? 'Save Filters' : 'Update Filters')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SegmentManagementModal;
